import type { TvPuttEvent, TvScene, TvSnapshot } from './protocol';
import { crownLineCupRing, kingCupMarkers, proximityBandCupRings } from '../../../packages/putt-scene/src/cupMarkers';
import { isSummaryDemoMode, summaryFixture } from './summaryFixtures';
import { levelDemoFixture, levelDemoMode } from './levelDemo';
import { TURF_WAR_DEAD_ZONES, TURF_WAR_ZONES, createTurfWarGame, type TurfWarState } from '../../../app/LagKing/src/multiplayerGames/turfWar';
import { turfWarRolloutBands } from '../../../app/LagKing/src/multiplayerGames/turfWarBands';
import { lineFourRolloutBands } from '../../../app/LagKing/src/multiplayerGames/lineFourBands';
import {
  BASEBALL_BUNT_HOLE_FT,
  BASEBALL_HIT_HOLES_FT,
  baseballBattingTeam,
  baseballBuntOn,
  baseballHitStages,
  createBaseballGame,
  type BaseballState,
} from '../../../app/LagKing/src/multiplayerGames/baseball';
import { BASEBALL_EXTRA_CHOICE_PHONE_ACTION, baseballDistanceMarkers, baseballExtraCups } from '../../../app/LagKing/src/multiplayerGames/baseballView';
import { SIM9_COURSES } from '../../../packages/putt-scene/src/courses';
import { applyCricketHit, createCricketGame, type CricketHit, type CricketState } from '../../../app/LagKing/src/multiplayerGames/cricket';
import { cricketDartBoard, cricketDartPopup, cricketRolloutBands, cricketTvGameState } from '../../../app/LagKing/src/multiplayerGames/cricketView';

type DemoMode =
  | 'target'
  | 'distance'
  | 'startline'
  | 'stepback'
  | 'ladder'
  | 'linefour'
  | 'linefourhandoff'
  | 'linefourresult'
  | 'baseball'
  | 'baseballfield'
  // Cole's 2026-10-18 notes: Baseball with mixed hit stages (1B at 1.5 ft,
  // 2B used up, 3B hole it, HR fresh), Turf War's sudden death, and Line
  // Four with a full column.
  | 'baseballstages'
  // Tied after three, waiting on the home phone's extra-innings choice (2026-10-19).
  | 'baseballtie'
  | 'turfwarsd'
  | 'linefourfull'
  // Football (2026-10-16): offense with two plays already seen, the defense
  // answering a converting short pass, and a touchdown moment.
  | 'football'
  | 'footballdefense'
  | 'footballtd'
  // The football field course at three spots (2026-10-16): own 20 (1st & 10),
  // midfield (3rd & 4), and 1st & goal at the opponent's 8 with the end zone
  // and posts in view.
  | 'footballfield'
  | 'footballfield-mid'
  | 'footballfield-goal'
  // Darts-Cricket (2026-10-20): the open board (two players), mid-game with
  // closed numbers (three players), a ball on the bull with its popup, a
  // TRIPLE 18 popup, and four players.
  | 'cricket'
  | 'cricketmid'
  | 'cricketbull'
  | 'cricketpopup'
  | 'cricket4'
  | 'turfwar'
  | 'turfwarhandoff'
  | 'turfwarresult'
  | 'poker'
  | 'pokerroll'
  | 'pokerreveal'
  // Presentation fixtures (TV_REVIVAL_PLAN.md §2.2): no phone needed to see
  // the mirror card, the ambient screen, a summary card, or the sleep hints.
  | 'mirror'
  | 'ambient'
  | 'summary'
  | 'asleep'
  // 2026-09-25 mode-map states: Target random, a distance race, Start Line
  // practice, a leaderboard card, a countdown card, and a ghost putt.
  | 'target-random'
  | 'distance-race'
  // King of the Hill with a King to race (bullet bar, no colored boxes) and
  // the month's first submission (no King: the boxes, no bar).
  | 'distance-koth'
  | 'distance-koth-first'
  // Today's Challenge on the fifth putt (2026-10-20): the crown ring at the
  // leave that takes #1 and today's #1's balls as ghosts, named in a chip.
  | 'distance-daily'
  | 'startline-practice'
  | 'board'
  // An event's standings between players, with the code to scan (2026-10-04).
  | 'event'
  | 'countdown'
  | 'ghost'
  // Interrupts (Part 2, "Interrupts, on every state"): a notice on a course,
  // a card, the mirror, and the ambient card, plus the low-battery pill.
  | 'notice-asleep'
  | 'notice-hit'
  | 'notice-readerror'
  | 'notice-confirm'
  | 'notice-battery'
  // Sim 9 as the app publishes it today (2026-09-25 detail sweep): solo
  // (Sim9PlayScreen, hideDefaultHud true), local stroke-play match
  // (Sim9StrokePlayScreen), the same match with the optional publishing-gap
  // fields filled in, and both scorecards.
  | 'sim9'
  | 'sim9-match'
  | 'sim9-match-full'
  | 'sim9-scorecard'
  | 'sim9-match-scorecard'
  | 'sim9-challenge-hole';

const DEMO_MODES: ReadonlySet<string> = new Set<DemoMode>([
  'target', 'distance', 'startline', 'stepback', 'ladder',
  'linefour', 'linefourhandoff', 'linefourresult',
  'baseball', 'baseballfield', 'baseballstages', 'baseballtie', 'turfwarsd', 'linefourfull', 'football', 'footballdefense', 'footballtd',
  'footballfield', 'footballfield-mid', 'footballfield-goal', 'cricket', 'cricketmid', 'cricketbull', 'cricketpopup', 'cricket4', 'turfwar', 'turfwarhandoff', 'turfwarresult',
  'poker', 'pokerroll', 'pokerreveal',
  'mirror', 'ambient', 'summary', 'asleep',
  'target-random', 'distance-race', 'distance-koth', 'distance-koth-first', 'distance-daily', 'startline-practice', 'board', 'event', 'countdown', 'ghost',
  'notice-asleep', 'notice-hit', 'notice-readerror', 'notice-confirm', 'notice-battery',
  'sim9', 'sim9-match', 'sim9-match-full', 'sim9-scorecard', 'sim9-match-scorecard', 'sim9-challenge-hole',
]);

const baseScene: Extract<TvScene, { kind: 'putt' }> = {
  kind: 'putt',
  targetFt: 42,
  stimp: 10,
  hideHole: false,
  hideFlag: false,
  targetLineFt: null,
  finishLineFt: null,
  finishLinePassed: false,
  rolloutBands: [],
  ballColor: '#ffffff',
  extraBalls: [],
  ballLabel: null,
  challengeProgress: null,
  avgProxFt: 2.4,
  showAvgProx: true,
  status: null,
};

// The lag modes (Target, Distance Challenge) send their proximity rings with
// the scene. The fixtures carry the same two, a 42 ft putt's green and yellow
// rings, so a ring that fails to draw shows up in ?demo=distance and in the
// Apple TV's -demo distance.
const DEMO_PROXIMITY_RINGS = proximityBandCupRings({ goodMaxFt: 2.1, midMaxFt: 4.2 });

/**
 * `&cards=N` on a Poker demo deals N cards to the hand (up to 15, a full
 * five-hole round of holed putts), so the long-hand layouts can be checked.
 * A real round reaches that; the default fixtures only ever held three or four.
 */
function demoCardCount(): number | null {
  const override = (globalThis as { __lkDemo?: { cards: number | null } }).__lkDemo;
  const search = typeof window !== 'undefined' && window.location ? window.location.search : '';
  const raw = override?.cards != null ? String(override.cards) : new URLSearchParams(search).get('cards');
  const count = raw == null ? NaN : Number(raw);
  return Number.isFinite(count) && count > 0 ? Math.min(15, Math.round(count)) : null;
}

function demoSolo(): boolean {
  if ((globalThis as { __lkDemo?: { solo: boolean } }).__lkDemo?.solo) return true;
  const search = typeof window !== 'undefined' && window.location ? window.location.search : '';
  return new URLSearchParams(search).has('solo');
}

function demoCards(count: number, offset = 0): Array<{ rank: number; suit: string }> {
  const suits = ['s', 'h', 'd', 'c'];
  return Array.from({ length: count }, (_, index) => ({
    rank: 14 - ((index * 3 + offset) % 13),
    suit: suits[(index + offset) % 4],
  }));
}

function event(targetFt = 42, distanceFt = 39.8): TvPuttEvent {
  return {
    kind: 'putt',
    id: `tv-${targetFt}-${distanceFt}`,
    createdAtMs: Date.now(),
    speedMps: 2.7,
    distanceFt,
    angleDeg: 0.35,
    proximityFt: Math.abs(distanceFt - targetFt),
    targetFt,
  };
}

export function demoFixture(
  rawMode: string | null,
  addressOnly = false,
  homeMenu = false,
): { snapshot: TvSnapshot; event: TvPuttEvent } {
  // ?demo=<mode>-rookie / -pro: the same game on a Rookie or Pro turn (levelDemo.ts).
  const levelDemo = levelDemoMode(rawMode);
  if (levelDemo) return levelDemoFixture(demoFixture(levelDemo.base, addressOnly, homeMenu), levelDemo.level);
  // ?demo=summary-<mode>: a phone summary screen's full TV layout (summaryFixtures.ts).
  const summaryMode = (rawMode ?? '').toLowerCase();
  if (isSummaryDemoMode(summaryMode)) return summaryFixture(summaryMode);
  const mode = normalizeMode(rawMode);
  if (mode.startsWith('footballfield')) return footballFieldFixture(mode);
  if (mode.startsWith('cricket')) return cricketFixture(mode, addressOnly);
  if (mode.startsWith('notice-')) return noticeFixture(mode);
  if (mode.startsWith('sim9')) return sim9Fixture(mode);
  const displayMode = mode === 'target-random'
    ? 'target'
    : mode === 'distance-race' || mode === 'distance-koth' || mode === 'distance-koth-first' || mode === 'distance-daily'
      ? 'distance'
    : mode === 'startline-practice' || mode === 'stepback'
      ? 'startline'
    : mode === 'linefourhandoff'
    ? 'linefour'
    : mode === 'linefourresult'
      ? 'linefour'
    : mode === 'turfwarhandoff'
      ? 'turfwar'
      : mode === 'turfwarresult'
        ? 'turfwar'
      : mode === 'pokerroll'
        ? 'poker'
      : mode === 'footballdefense' || mode === 'footballtd'
        ? 'football'
      : mode;
  const snapshot: TvSnapshot = {
    mode: displayMode === 'distance' ? 'challenge' : displayMode,
    title: displayMode.toUpperCase(),
    subtitle: 'PRACTICE GREEN',
    playerName: 'COLE',
    targetFt: 42,
    gateConnected: true,
    scene: displayMode === 'target' ? { ...baseScene, hideGimme: true, cupRings: DEMO_PROXIMITY_RINGS } : { ...baseScene },
  };

  if (mode === 'mirror' && homeMenu) {
    snapshot.title = 'Challenge';
    snapshot.subtitle = 'Choose a mode';
    snapshot.scene = null;
    snapshot.sleep = 'touch';
    snapshot.phoneAwake = true;
    snapshot.presentation = { kind: 'mirror', routeId: 'challenge.home', inputOwner: 'phone' };
    snapshot.menu = challengeHomeMenu();
    return { snapshot, event: event() };
  }

  if (mode === 'mirror') {
    snapshot.title = 'Distance Challenge';
    snapshot.subtitle = 'Pick a distance';
    snapshot.scene = null;
    snapshot.sleep = 'touch';
    snapshot.phoneAwake = true;
    snapshot.presentation = { kind: 'mirror', routeId: 'challenge.distance.setup', inputOwner: 'phone' };
    snapshot.menu = {
      id: 'challenge.distance.setup',
      heading: 'DISTANCE',
      options: [
        { id: '20', label: '20 FT' },
        { id: '30', label: '30 FT', emphasis: 'selected' },
        { id: '40', label: '40 FT' },
        { id: '50', label: '50 FT' },
        { id: 'start', label: 'Start', detail: 'Five putts', emphasis: 'primary' },
      ],
    };
    return { snapshot, event: event() };
  }

  if (mode === 'target-random') {
    snapshot.title = 'Target';
    snapshot.targetFt = 27;
    snapshot.presentation = { kind: 'course', routeId: 'target', inputOwner: 'phone' };
    snapshot.sleep = 'putting';
    snapshot.scene = { ...baseScene, targetFt: 27, avgProxFt: 1.9 };
    snapshot.game = {
      kind: 'target',
      targetFt: 27,
      startLineLabel: 'PURE 👑',
      avgProxFt: 1.9,
      puttCount: 2,
      lastLeaveFt: 1.4,
      random: { index: 2, total: 6, puttsPerTarget: 3 },
    };
    return { snapshot, event: event(27, 25.6) };
  }

  if (mode === 'distance-race') {
    snapshot.title = 'Distance Challenge';
    snapshot.mode = 'challenge';
    snapshot.targetFt = 32;
    snapshot.playerName = 'Cole';
    snapshot.presentation = { kind: 'course', routeId: 'challenge.distance.friend.round', inputOwner: 'phone' };
    snapshot.sleep = 'putting';
    snapshot.scene = {
      ...baseScene,
      targetFt: 32,
      ballColor: '#c8922a',
      avgProxFt: 2.3,
      extraBalls: [
        { xFt: -0.4, zFt: -30.1, color: '#4fc3f7', label: '1.9' },
        { xFt: 0.6, zFt: -34.2, color: '#4fc3f7', label: '2.2' },
        { xFt: 0.1, zFt: -31.4, color: '#4fc3f7', label: '0.6' },
      ],
    };
    snapshot.game = {
      kind: 'distance',
      mode: 'friend',
      targetFt: 32,
      leavesFt: [1.8, 3.1, 1.9, null, null],
      puttsTotal: 5,
      avgProxFt: 2.3,
      playerName: 'Cole',
      playerColor: '#c8922a',
      vsLabel: 'VS @KELLYF',
      race: {
        label: '@KELLYF',
        theirAvgFt: 1.6,
        yourAvgFt: 2.3,
        theirLeavesFt: [1.9, 2.2, 0.6, 1.4, 1.9],
      },
    };
    return { snapshot, event: event(32, 30.1) };
  }

  if (mode === 'distance-koth' || mode === 'distance-koth-first') {
    // KothRoundScreen: one putt per hole, the target moves each hole. The
    // King's per-hole leaves ride in race.theirLeavesFt (tvRaceBarFromKingRace);
    // the month's first submission has no King, so no race.
    const first = mode === 'distance-koth-first';
    snapshot.title = 'King of the Hill';
    snapshot.mode = 'challenge';
    snapshot.targetFt = 24;
    snapshot.playerName = 'Cole';
    snapshot.presentation = { kind: 'course', routeId: 'challenge.koth.round', inputOwner: 'phone' };
    snapshot.sleep = 'putting';
    const theirLeavesFt = [1.2, 3.0, 5.5, 2.1, 0.0];
    // What KothRoundScreen sends: the king's ring for this hole (hole 3, the
    // king left 5.5 ft), and on a `&crown=1` demo the crown line beside it.
    const kingRings = first ? [] : kingCupMarkers(theirLeavesFt, 2).cupRings;
    const crown = (globalThis as { __lkDemo?: { crown?: boolean } }).__lkDemo?.crown === true
      || (typeof window !== 'undefined' && !!window.location && new URLSearchParams(window.location.search).has('crown'));
    snapshot.scene = {
      ...baseScene, targetFt: 24, avgProxFt: null, showAvgProx: false, hideGimme: true,
      cupRings: crown ? [crownLineCupRing(3.2)] : kingRings,
    };
    snapshot.game = {
      kind: 'distance',
      mode: 'koth',
      targetFt: 24,
      leavesFt: [0.9, 3.4, null, null, null],
      puttsTotal: 5,
      avgProxFt: 2.15,
      playerName: 'Cole',
      race: first ? null : {
        label: 'THE KING',
        theirAvgFt: theirLeavesFt.reduce((a, b) => a + b, 0) / theirLeavesFt.length,
        yourAvgFt: 2.15,
        theirLeavesFt,
      },
      hole: { index: 3, total: 5 },
    };
    return { snapshot, event: event(24, 20.6) };
  }

  if (mode === 'distance-daily') {
    // DailyChallengeScreen through ChallengeLandscape: four putts down for
    // 6.0 ft against today's #1 at a 1.44 ft average (7.2 ft for five), so
    // the fifth must finish inside 1.2 ft. The leader's five rests are gray
    // ghost balls (holed putt 3 draws none); the player's own earlier balls
    // stay white where they stopped.
    snapshot.title = "Today's Challenge";
    snapshot.mode = 'challenge';
    snapshot.targetFt = 25;
    snapshot.playerName = 'Cole';
    snapshot.presentation = { kind: 'course', routeId: 'challenge.distance.daily.round', inputOwner: 'phone' };
    snapshot.sleep = 'putting';
    const ghost = '#9AA4B2';
    const leaderLabel = '@KELLYF · #1';
    snapshot.scene = {
      ...baseScene,
      targetFt: 25,
      avgProxFt: 1.5,
      hideGimme: true,
      cupRings: [crownLineCupRing(1.2)],
      extraBalls: [
        { xFt: 0.5, zFt: -26.3, color: '#ffffff', label: '1' },
        { xFt: -0.6, zFt: -24.2, color: '#ffffff', label: '2' },
        { xFt: 1.1, zFt: -26.8, color: '#ffffff', label: '3' },
        { xFt: -0.4, zFt: -23.6, color: '#ffffff', label: '4' },
        { xFt: -0.9, zFt: -25.6, color: ghost, label: leaderLabel },
        { xFt: 0.3, zFt: -23.9, color: ghost, label: leaderLabel },
        { xFt: 0.8, zFt: -27.7, color: ghost, label: leaderLabel },
        { xFt: -0.6, zFt: -27.0, color: ghost, label: leaderLabel },
      ],
    };
    snapshot.game = {
      kind: 'distance',
      mode: 'daily',
      targetFt: 25,
      // The fifth leave is the rolling ball's (0.9 ft, inside the crown);
      // the HUD conceals it until the ball settles.
      leavesFt: [1.4, 1.0, 2.1, 1.5, 0.9],
      puttsTotal: 5,
      avgProxFt: 1.38,
      playerName: 'Cole',
      race: {
        label: '@KELLYF',
        theirAvgFt: 1.44,
        yourAvgFt: 1.5,
        theirLeavesFt: [1.1, 1.2, 0, 2.8, 2.1],
      },
      ghostLegend: { label: leaderLabel, color: ghost },
    };
    return { snapshot, event: { ...event(25, 25.9), id: 'tv-daily-fifth', angleDeg: 0.1 } };
  }

  if (mode === 'startline-practice') {
    snapshot.title = 'Start Line';
    snapshot.mode = 'startline';
    snapshot.targetFt = 10;
    snapshot.presentation = { kind: 'course', routeId: 'challenge.startline.practice', inputOwner: 'phone' };
    snapshot.sleep = 'putting';
    snapshot.scene = { ...baseScene, targetFt: 10, hideHole: true, hideFlag: true, hideGimme: true, hideProxLabel: true, avgProxFt: null, showAvgProx: false };
    snapshot.game = {
      kind: 'startLine',
      mode: 'practice',
      distanceFt: 10,
      streak: 0,
      best: 0,
      result: null,
      grade: 'SLIGHT',
      direction: 'right',
      totalRollFt: 12.4,
    };
    return { snapshot, event: { ...event(10, 12.4), id: 'tv-startline-practice', angleDeg: 1.1, proximityFt: null } };
  }

  if (mode === 'event') {
    // What the Events kiosk publishes while nobody is putting (EventKiosk.tsx).
    snapshot.title = 'Spring Open';
    snapshot.mode = 'challenge';
    snapshot.scene = null;
    snapshot.sleep = 'touch';
    snapshot.presentation = { kind: 'card', routeId: 'challenge.event', inputOwner: 'phone' };
    const standings = [
      ['#12 Sam T.', '28 pts'], ['#5 Ann Lee', '26 pts'], ['#31 Bob', '24 pts'], ['#8 Priya K.', '22 pts'],
      ['#19 Dee', '20 pts'], ['#2 Marcus W.', '18 pts'], ['#27 Jo', '16 pts'], ['#14 Chris P.', '14 pts'],
    ];
    snapshot.summary = {
      kicker: 'EVENT',
      title: 'Spring Open',
      headline: 'Step up and putt',
      rows: [],
      columns: [1, 1.1],
      // As the kiosk sends it: the games as a table, the standings beside it.
      sections: [
        {
          kind: 'text',
          column: 0,
          align: 'center',
          lines: [
            { text: 'EVENT', color: '#8FA1BC', size: 'caption', weight: 'bold', spacing: 0.15 },
            { text: 'Spring Open', size: 'title', weight: 'heavy' },
            { text: 'Step up and putt', color: '#C8922A', size: 'value', weight: 'heavy' },
          ],
        },
        {
          kind: 'entries',
          column: 0,
          title: 'THE GAMES',
          rows: [
            { rank: '1', rankColor: '#8FA1BC', name: 'Lag', detail: '2 putts each at 20 and 40 ft', detailColor: '#C9D3E2', value: 'Lowest total miss', valueColor: '#C8922A' },
            { rank: '2', rankColor: '#8FA1BC', name: 'Start Line', detail: '5 putts from 8 ft', detailColor: '#C9D3E2', value: 'Most sunk', valueColor: '#C8922A' },
            { rank: '3', rankColor: '#8FA1BC', name: 'Poker', detail: '3 holes from 15 to 50 ft · normal rings', detailColor: '#C9D3E2', value: 'Best hand', valueColor: '#C8922A' },
          ],
        },
        {
          kind: 'text',
          column: 0,
          align: 'center',
          lines: [{ text: 'One attempt  ·  Points for your rank in each game', color: '#C9D3E2', size: 'caption', weight: 'bold' }],
        },
        {
          kind: 'entries',
          column: 1,
          title: '37 PLAYERS',
          rows: standings.map(([name, value], index) => ({
            rank: String(index + 1),
            rankColor: '#8FA1BC',
            name,
            value,
            border: index === 0 ? '#C8922A' : null,
          })),
        },
      ],
      qr: { url: 'https://lagking.com/events/?e=abcdefghjk', label: 'Scan for the leaderboard' },
    };
    return { snapshot, event: event() };
  }

  if (mode === 'board') {
    snapshot.title = 'Distance Challenge';
    snapshot.mode = 'challenge';
    snapshot.scene = null;
    snapshot.sleep = 'touch';
    snapshot.presentation = { kind: 'card', routeId: 'challenge.distance.leaderboard', inputOwner: 'phone' };
    snapshot.summary = {
      kicker: 'DISTANCE CHALLENGE · 30 FT',
      title: 'Leaderboard',
      rows: [],
      board: {
        title: 'ALL TIME · 30 FT',
        rows: [
          { rank: 1, name: '@puttmaster', value: '0.9 FT' },
          { rank: 2, name: '@kellyf', value: '1.1 FT' },
          { rank: 3, name: '@lagqueen', value: '1.3 FT' },
          { rank: 4, name: '@birdiebill', value: '1.4 FT' },
          { rank: 5, name: '@greenreader', value: '1.6 FT' },
          { rank: 6, name: '@stimp12', value: '1.7 FT' },
          { rank: 7, name: '@threeputt', value: '1.9 FT' },
          { rank: 8, name: '@rollit', value: '2.0 FT' },
          { rank: 9, name: '@lipout', value: '2.2 FT' },
          { rank: 10, name: '@sandy', value: '2.3 FT' },
          { rank: 11, name: '@overflow', value: '2.4 FT' },
          { rank: 14, name: 'Cole', value: '2.8 FT', you: true },
        ],
      },
      footer: 'Choose on your device.',
    };
    return { snapshot, event: event() };
  }

  if (mode === 'countdown') {
    snapshot.title = 'Simulated 9';
    snapshot.mode = 'sim9';
    snapshot.presentation = { kind: 'card', routeId: 'challenge.sim9.hole-done', inputOwner: 'phone' };
    snapshot.sleep = 'putting';
    snapshot.scene = { ...baseScene, targetFt: 24 };
    snapshot.transition = {
      modeTitle: 'SIM 9 · PEBBLE',
      kicker: 'HOLE 3 COMPLETE',
      title: 'Birdie',
      detail: '2 putts from 24 ft',
      stats: [
        { label: 'THIS HOLE', value: '2', tone: 'success' },
        { label: 'TO PAR', value: '-1', tone: 'gold' },
        { label: 'TOTAL', value: '7' },
      ],
      actionLabel: 'NEXT HOLE',
      countdownEndsAtMs: Date.now() + 5000,
      countdownLabel: 'Next hole',
    };
    return { snapshot, event: event(24, 23.2) };
  }

  if (mode === 'ghost') {
    snapshot.title = 'Simulated 9';
    snapshot.mode = 'sim9';
    snapshot.targetFt = 31;
    snapshot.presentation = { kind: 'course', routeId: 'challenge.sim9.friend.replay', inputOwner: 'phone' };
    snapshot.sleep = 'putting';
    snapshot.scene = { ...baseScene, targetFt: 31, ballColor: '#c8922a', avgProxFt: null, showAvgProx: false };
    snapshot.game = {
      kind: 'sim9',
      courseName: 'PEBBLE',
      hole: 4,
      holes: 9,
      par: 2,
      requiredFt: 31,
      strokes: 0,
      status: 'E',
      scoreLabel: 'ROUND',
      playerName: 'Cole',
    };
    return {
      snapshot,
      event: {
        ...event(31, 29.4),
        id: 'tv-ghost-kelly',
        ghost: true,
        playerName: '@kellyf',
        color: '#4fc3f7',
        angleDeg: -0.8,
      },
    };
  }

  if (mode === 'ambient') {
    snapshot.title = 'LagKing';
    snapshot.subtitle = '';
    snapshot.scene = null;
    snapshot.sleep = 'touch';
    snapshot.phoneAwake = true;
    return { snapshot, event: event() };
  }

  if (mode === 'summary') {
    snapshot.title = 'Distance Challenge';
    snapshot.scene = null;
    snapshot.sleep = 'touch';
    snapshot.presentation = { kind: 'card', routeId: 'challenge.distance.summary', inputOwner: 'phone' };
    snapshot.summary = {
      kicker: 'DISTANCE CHALLENGE · 30 FT',
      title: 'Round complete',
      headline: '2.1 FT',
      headlineDetail: 'average leave',
      rows: [
        { label: 'PUTT 1', value: '1.2 FT', tone: 'success' },
        { label: 'PUTT 2', value: '2.7 FT' },
        { label: 'PUTT 3', value: '4.1 FT', tone: 'danger' },
        { label: 'PUTT 4', value: '0.9 FT', tone: 'gold' },
        { label: 'PUTT 5', value: '1.6 FT' },
      ],
      footer: 'Choose on your device.',
    };
    return { snapshot, event: event() };
  }

  if (mode === 'asleep') {
    snapshot.title = 'Target';
    snapshot.sleep = 'putting';
    snapshot.phoneAwake = false;
    snapshot.presentation = { kind: 'course', routeId: 'target', inputOwner: 'phone' };
    return { snapshot, event: event() };
  }

  if (mode === 'distance') {
    snapshot.title = 'Distance Challenge';
    snapshot.scene = {
      ...baseScene,
      // As the phone sends it: no gimme circle, the two proximity rings.
      hideGimme: true,
      cupRings: DEMO_PROXIMITY_RINGS,
      challengeProgress: [1.2, 2.7, -4.1, null, null],
      avgProxFt: 2.7,
    };
  }

  if (mode === 'startline') {
    snapshot.targetFt = 10;
    snapshot.scene = { ...baseScene, targetFt: 10, hideFlag: true, hideGimme: true, hideProxLabel: true, avgProxFt: null };
    snapshot.game = {
      kind: 'startLine',
      distanceFt: 10,
      streak: 7,
      best: 12,
      result: { label: 'IN 👑', detail: 'IN THE CUP', tone: 'success' },
      startLineLabel: 'PURE 👑',
      totalRollFt: 10.1,
    };
    return { snapshot, event: { ...event(10, 10.1), hasHoled: true, holed: true } };
  }

  if (mode === 'stepback') {
    // Step Back, three makes in: a 7 ft putt on the fixed 12 ft hole
    // (stageFrame.ts), the ball addressed 5 ft down it.
    snapshot.targetFt = 7;
    snapshot.scene = { ...baseScene, targetFt: 7, hideFlag: true, hideGimme: true, hideProxLabel: true, avgProxFt: null };
    snapshot.game = {
      kind: 'startLine',
      distanceFt: 7,
      streak: 3,
      best: 5,
      stepBack: true,
      farthestFt: 6,
      bestFarthestFt: 8,
      result: { label: 'IN 👑', detail: 'IN THE CUP', tone: 'success' },
      startLineLabel: 'PURE 👑',
      totalRollFt: 7.1,
    };
    return { snapshot, event: { ...event(7, 7.1), hasHoled: true, holed: true } };
  }

  if (mode === 'ladder') {
    const extraBalls = [
      { xFt: -.2, zFt: -15.2, color: '#c8922a', label: '15.2' },
      { xFt: .15, zFt: -18.7, color: '#d8ae5b', label: '18.7' },
      { xFt: -.1, zFt: -23.1, color: '#ece0c4', label: '23.1' },
    ];
    snapshot.targetFt = 40;
    snapshot.scene = {
      ...baseScene,
      targetFt: 40,
      hideHole: true,
      hideFlag: true,
      targetLineFt: 23.1,
      finishLineFt: 40,
      extraBalls,
      ballLabel: '+2.4 FT',
      avgProxFt: null,
    };
    snapshot.game = {
      kind: 'ladder',
      variant: 'solo',
      phase: 'climbing',
      climbs: 3,
      lowFt: 15,
      highFt: 40,
      targetLineFt: 23.1,
      finishLineFt: 40,
      shotCount: 4,
    };
    return { snapshot, event: event(40, 25.5) };
  }

  if (mode === 'linefour' || mode === 'linefourhandoff' || mode === 'linefourresult' || mode === 'linefourfull') {
    // linefourfull: column D filled to the top, so its zone is blacked out
    // and its legend row crossed out (2026-10-18).
    const board: Array<Array<0 | 1 | null>> = mode === 'linefourfull'
      ? [
          [null, 1, null, 0, 1, null, null],
          [null, null, null, 1, 0, null, null],
          [null, null, null, 0, null, null, null],
          [null, null, null, 1, null, null, null],
          [null, null, null, 0, null, null, null],
          [null, null, null, 1, null, null, null],
        ]
      : [
          [null, 1, null, 0, 1, null, null],
          [null, null, null, 0, null, null, null],
          [null, null, null, null, null, null, null],
          [null, null, null, null, null, null, null],
          [null, null, null, null, null, null, null],
          [null, null, null, null, null, null, null],
        ];
    // The phone's bands (lineFourRolloutBands): standing "A · 15-20 FT" labels.
    const bands = lineFourRolloutBands(board);
    snapshot.scene = { ...baseScene, targetFt: 50, hideHole: true, hideFlag: true, rolloutBands: bands, ballColor: '#c8922a' };
    snapshot.game = {
      kind: 'lineFour',
      names: ['Cole', 'Computer'],
      phase: mode === 'linefourhandoff' ? 'handoff' : mode === 'linefourresult' ? 'result' : 'putt',
      activePlayerIndex: 0,
      state: {
        board,
        attemptCount: mode === 'linefourfull' ? 10 : 5,
        winnerIndex: mode === 'linefourresult' ? 0 : null,
        winningCells: mode === 'linefourresult'
          ? [{ row: 0, column: 3 }, { row: 1, column: 3 }, { row: 2, column: 3 }, { row: 3, column: 3 }]
          : [],
        isDraw: false,
      },
    };
    if (mode === 'linefourresult') {
      snapshot.menu = {
        id: 'challenge.linefour.result',
        options: [
          { id: 'rematch', label: 'Rematch', emphasis: 'primary' },
          { id: 'exit', label: 'Exit' },
        ],
      };
    }
  }

  if (mode === 'baseball' || mode === 'baseballfield' || mode === 'baseballstages' || mode === 'baseballtie') {
    // The phone's cups and standing markers (baseballView.ts), from a game
    // state: top 2, one out, runners on first and third. Batting: the bunt
    // cup and the sacrifice-fly rings are on. Fielding: the batter's double
    // lit with a gold ring at the batter's 1.8 ft leave. baseballstages: the
    // away team's single at 1.5 ft, its double used up (no cup or ring, a
    // marker; the ball rolls over the spot), its triple hole-it, its
    // home run fresh (Cole, 2026-10-18).
    const fielding = mode === 'baseballfield';
    const staged = mode === 'baseballstages';
    const tie = mode === 'baseballtie';
    const state: BaseballState = {
      ...createBaseballGame(3),
      inning: 2,
      half: 'top',
      outs: 1,
      bases: [true, false, true],
      runs: [1, 2],
      lineScore: [[1, 0], [2]],
      atBats: 7,
      phase: fielding ? 'fielding' : 'batting',
      pending: fielding ? { kind: 'hit', hole: 25, leaveFt: 1.8 } : null,
      hitStages: [staged ? [1, 3, 2, 0] : [0, 0, 0, 0], [0, 0, 0, 0]],
    };
    snapshot.scene = {
      ...baseScene,
      targetFt: 45,
      hideHole: true,
      hideFlag: true,
      ballColor: fielding ? '#ffffff' : '#f0605d',
      extraCups: baseballExtraCups(state),
      rolloutBands: baseballDistanceMarkers(state),
      extraBalls: fielding ? [{ xFt: 0.9, zFt: -26.5, color: '#f0605d' }] : [],
      // The batting fixture's ball (36.4 ft, on line) drops in the triple's
      // cup; the staged one (25.6 ft) rolls over the used-up double's spot.
      holedAtFt: fielding || staged ? null : 35,
    };
    snapshot.game = {
      kind: 'baseball',
      names: ['Kelly', 'Cole'],
      colors: ['#f0605d', '#ffffff'],
      holesFt: [...BASEBALL_HIT_HOLES_FT],
      buntFt: baseballBuntOn(state) ? BASEBALL_BUNT_HOLE_FT : null,
      hitStages: [...baseballHitStages(state, baseballBattingTeam(state))],
      phase: 'putt',
      role: fielding ? 'fielding' : 'batting',
      putterTeam: fielding ? 1 : 0,
      inningLabel: 'TOP 2',
      inning: 2,
      innings: 3,
      half: 'top',
      outs: 1,
      bases: [true, false, true],
      runs: [1, 2],
      lineScore: { away: [1, 0], home: [2] },
      pending: fielding ? { kind: 'hit', hole: 25, leaveFt: 1.8, bases: 2 } : null,
      lastPlay: { kind: 'single', team: 0, moves: [{ from: 2, to: 3 }, { from: 0, to: 1 }], runs: 0 },
      atBats: 7,
      status: fielding ? 'GET INSIDE 1.8 FT FROM 25 FT' : 'SAC BUNT OR SAC FLY AVAILABLE',
    };
    // The phone's prompt while the tie waits on the home seat (BaseballHost);
    // every game kind may carry one (phonePromptModel.ts).
    if (tie) (snapshot.game as Record<string, unknown>).phonePrompt = { player: 'Cole', action: BASEBALL_EXTRA_CHOICE_PHONE_ACTION, color: '#FFFFFF' };
    return { snapshot, event: event(25, fielding ? 26.1 : staged ? 25.6 : 36.4) };
  }

  if (mode === 'football' || mode === 'footballdefense' || mode === 'footballtd') {
    // v5 (FootballHost footballExtraCups and footballFieldInput): five cups
    // at 9 / 18 / 27 / 36 / 45 ft on the field at the game's spot. Kelly's
    // short pass is seen once (1.5 ft, the full ring dashed), the medium pass
    // twice (hole it), the deep pass is gone. Defending: the short pass's cup
    // lit with a gold ring at Kelly's ball (1.4 ft away).
    const defending = mode === 'footballdefense';
    const touchdown = mode === 'footballtd';
    const cups = [
      { id: 'cup-inside', distanceFt: 9, ringRadiusFt: 3, color: '#f5f8eb', opacity: 0.75 },
      { id: 'cup-outside', distanceFt: 18, ringRadiusFt: 3, color: '#f5f8eb', opacity: 0.75 },
      { id: 'cup-short', distanceFt: 27, ringRadiusFt: 1.5, color: '#cfe3ff', opacity: 0.75 },
      { id: 'cup-medium', distanceFt: 36, color: '#cfe3ff', opacity: 0.95, lit: true },
    ];
    const freshCups = [9, 18, 27, 36, 45].map((ft, i) => ({ id: `cup-${['inside', 'outside', 'short', 'medium', 'deep'][i]}`, distanceFt: ft, ringRadiusFt: 3, color: i < 2 ? '#f5f8eb' : '#cfe3ff', opacity: 0.75 }));
    snapshot.scene = {
      ...baseScene,
      targetFt: defending ? 27 : 45,
      hideHole: true,
      hideFlag: true,
      ballColor: defending ? '#ffffff' : touchdown ? '#ffffff' : '#f0605d',
      // Football's cups are round dark holes with a white rim (holeStyle 'field', 2026-10-18).
      extraCups: (defending
        ? [
            // The answer cup wears the white flag, as the phone sends it (footballView.ts).
            { id: 'cup-short', distanceFt: 27, color: '#c8922a', opacity: 0.9, lit: true, flag: true, flagColor: '#FFFFFF' },
            { id: 'answer', distanceFt: 27, ringRadiusFt: 1.4, color: '#c8922a', opacity: 0.95, ringOnly: true },
          ]
        : touchdown ? freshCups : cups).map((cup) => ('ringOnly' in cup ? cup : { ...cup, holeStyle: 'field' as const })),
      extraBalls: defending ? [{ xFt: 0.6, zFt: -26.4, color: '#f0605d' }] : [],
      // On the football field at this game's spot: Kelly at the opponent's 38,
      // 3rd & 6; after Kelly's touchdown, Cole from the opponent's 35.
      footballField: touchdown
        ? { scrimmageYl: 65, firstDownYl: 75, aheadEndZone: { color: '#f0605d', name: 'KELLY' }, behindEndZone: { color: '#ffffff', name: 'COLE' } }
        : { scrimmageYl: 62, firstDownYl: 68, aheadEndZone: { color: '#ffffff', name: 'COLE' }, behindEndZone: { color: '#f0605d', name: 'KELLY' } },
    };
    const table = (rows: Array<[string, string, string, string, string]>) =>
      rows.map(([key, label, distance, need, tone]) => ({ key, label, distance, need, tone }));
    snapshot.game = {
      kind: 'football',
      names: ['Kelly', 'Cole'],
      colors: ['#f0605d', '#ffffff'],
      scores: touchdown ? [14, 10] : [7, 10],
      phase: 'putt',
      role: defending ? 'defense' : 'offense',
      // The phone sends the distance the defense answers from (footballTargetFt).
      targetFt: defending ? 27 : null,
      offense: touchdown ? 1 : 0,
      putterTeam: defending ? 1 : touchdown ? 1 : 0,
      driveLabel: '2ND HALF',
      clockLabel: touchdown ? 'DRIVE 2 OF 3' : 'DRIVE 2 OF 3',
      half: 2,
      drivesLeft: 3,
      lastSnapOfHalf: false,
      overtime: 0,
      down: touchdown ? 1 : 3,
      togo: touchdown ? 10 : 6,
      yl: touchdown ? 65 : 62,
      downLabel: touchdown ? '1ST & 10 . OPP 35' : '3RD & 6 . OPP 38',
      twoPoint: false,
      // The plays table (app footballPlayTable, v5): PLAY | CUP | NEEDS.
      table: touchdown
        ? table([
            ['inside', 'INSIDE RUN', '9 FT', '3 FT', 'fresh'],
            ['outside', 'OUTSIDE RUN', '18 FT', '1.5 FT', 'seen'],
            ['short', 'SHORT PASS', '27 FT', '3 FT', 'fresh'],
            ['medium', 'MEDIUM PASS', '36 FT', '3 FT', 'fresh'],
            ['deep', 'DEEP PASS', '45 FT', '3 FT', 'fresh'],
          ])
        : table([
            ['inside', 'INSIDE RUN', '9 FT', '3 FT', defending ? 'dim' : 'fresh'],
            ['outside', 'OUTSIDE RUN', '18 FT', '3 FT', defending ? 'dim' : 'fresh'],
            ['short', 'SHORT PASS', '27 FT', defending ? 'INSIDE 1.4 FT' : '1.5 FT', defending ? 'answer' : 'seen'],
            ['medium', 'MEDIUM PASS', '36 FT', 'HOLE IT', defending ? 'dim' : 'hole'],
            ['deep', 'DEEP PASS', '45 FT', 'GONE', 'gone'],
          ]),
      status: defending
        ? 'BEAT 0.4 FT AT THE 27 FT CUP'
        : touchdown ? 'HOLED · DEEP PASS · 25 YARDS · TOUCHDOWN' : 'SHORT PASS · 9 YARDS · FIRST DOWN',
      lastSnap: touchdown
        ? { team: 0, fromYl: 75, toYl: 100, yards: 25, result: 'completeHoled', label: 'HOLED', line: 'HOLED · DEEP PASS · 25 YARDS', touchdown: true, turnover: false, firstDown: false }
        : defending
          ? null
          : { team: 0, fromYl: 53, toYl: 62, yards: 9, result: 'complete', label: 'COMPLETE', line: 'SHORT PASS · 9 YARDS', touchdown: false, turnover: false, firstDown: true },
      snapKey: touchdown ? 41 : 40,
      moment: touchdown ? { kind: 'touchdown', team: 0, key: 5 } : null,
      winner: null,
    };
    return { snapshot, event: event(27, defending ? 26.4 : 27.3) };
  }

  if (mode === 'turfwar' || mode === 'turfwarhandoff' || mode === 'turfwarresult') {
    // The phone's bands (TurfWarHost rolloutBandsFor): five-foot scoring
    // bands, owner-tinted, then the five-foot dead gaps shaded dark.
    const scoringBands = TURF_WAR_ZONES.map((zone) => {
      const owner = zone.index === 1 || zone.index === 3 ? 0 : zone.index === 2 ? 1 : null;
      const color = owner === 0 ? '#f0605d' : owner === 1 ? '#4fc3f7' : '#dba94d';
      return {
        id: `turf-${zone.index}`,
        minFt: zone.minFt,
        maxFt: zone.maxFt,
        label: `${zone.minFt}-${zone.maxFt} FT`,
        labelOffsetFt: -4,
        fillColor: owner == null ? undefined : color,
        fillOpacity: owner == null ? 0 : 0.24,
        lineColor: color,
      };
    });
    const deadBands = TURF_WAR_DEAD_ZONES.map((gap, index) => ({
      id: `turf-dead-${index}`,
      minFt: gap.minFt,
      maxFt: gap.maxFt,
      label: '',
      fillColor: '#05070C',
      fillOpacity: 0.55,
      lineColor: '#05070C',
    }));
    const turfBands = [...scoringBands, ...deadBands];
    snapshot.scene = { ...baseScene, targetFt: 60, hideHole: true, hideFlag: true, ballColor: '#f0605d', rolloutBands: turfBands };
    snapshot.game = {
      kind: 'turfWar',
      names: ['Cole', 'Computer'],
      phase: mode === 'turfwarhandoff' ? 'handoff' : mode === 'turfwarresult' ? 'result' : 'putt',
      activePlayerIndex: 0,
      score: [2, 1],
      state: {
        phase: mode === 'turfwarresult' ? 'complete' : 'regulation',
        regulationPutts: mode === 'turfwarresult' ? [10, 10] : [6, 5],
        shots: [
          { playerIndex: 0, zoneIndex: 1 }, { playerIndex: 0, zoneIndex: 1 },
          { playerIndex: 1, zoneIndex: 1 }, { playerIndex: 1, zoneIndex: 2 },
          { playerIndex: 0, zoneIndex: 2 }, { playerIndex: 0, zoneIndex: 3 },
        ],
        winnerIndex: mode === 'turfwarresult' ? 0 : null,
      },
    };
    if (mode === 'turfwarresult') {
      snapshot.menu = {
        id: 'challenge.turfwar.result',
        options: [
          { id: 'rematch', label: 'Rematch', emphasis: 'primary' },
          { id: 'exit', label: 'Exit' },
        ],
      };
    }
  }

  if (mode === 'turfwarsd') {
    // Regulation ended 2-2; the playoff picked 20-25 FT. Only that zone stays
    // lit with its label; every other zone and gap is blacked out (2026-10-18).
    const shot = (playerIndex: 0 | 1, zoneIndex: number | null) => ({ playerIndex, zoneIndex, distanceFt: zoneIndex === null ? 12 : TURF_WAR_ZONES[zoneIndex].midpointFt, phase: 'regulation' as const, suddenDeathRound: null });
    const state: TurfWarState = {
      ...createTurfWarGame(0),
      phase: 'suddenDeath',
      regulationPutts: [10, 10],
      shots: [
        ...[0, 0, 0, 2, 2].map((zone) => shot(0, zone)),
        ...[1, 1, 1, 3, 3].map((zone) => shot(1, zone)),
        ...Array.from({ length: 5 }, () => shot(0, null)),
        ...Array.from({ length: 5 }, () => shot(1, null)),
      ],
      suddenDeath: { zoneIndex: 1, round: 1, openerIndex: 0, attempts: [null, null] },
    };
    snapshot.scene = {
      ...baseScene,
      targetFt: 60,
      hideHole: true,
      hideFlag: true,
      ballColor: '#f0605d',
      rolloutBands: turfWarRolloutBands(state, { players: ['#f0605d', '#4fc3f7'], open: '#dba94d' }),
    };
    snapshot.game = {
      kind: 'turfWar',
      names: ['Cole', 'Computer'],
      phase: 'putt',
      activePlayerIndex: 0,
      score: [2, 2],
      state: {
        phase: 'suddenDeath',
        regulationPutts: [10, 10],
        shots: state.shots,
        suddenDeath: state.suddenDeath!,
        winnerIndex: null,
      },
    };
    return { snapshot, event: event(22.5, 23.1) };
  }

  if (mode === 'poker' || mode === 'pokerroll') {
    const withinFt = 2 + ((30 - 15) / (50 - 15)) * (6 - 2);
    const closeFt = withinFt * 0.5;
    snapshot.scene = {
      ...baseScene,
      targetFt: 30,
      ballColor: '#c8922a',
      gimmeRadiusFt: withinFt,
      innerGimmeRadiusFt: closeFt,
    };
    snapshot.game = {
      kind: 'poker',
      // &solo=1: the Daily Poker layout (one player, one hand panel).
      names: demoSolo() ? ['Cole', ''] : ['Cole', 'Computer'],
      hole: 4,
      holes: 5,
      playerName: 'Cole',
      distanceFt: 30,
      closeFt,
      withinFt,
      handCounts: [demoCardCount() ?? 3, demoSolo() ? 0 : 2],
      activeHand: demoCardCount()
        ? demoCards(demoCardCount() as number)
        : [{ rank: 'A', suit: '♠' }, { rank: '10', suit: '♥' }, { rank: 'K', suit: '♦' }],
      phase: addressOnly || mode === 'pokerroll' ? 'putt' : 'award',
      awardCards: addressOnly || mode === 'pokerroll' ? null : [{ rank: 12, suit: 'h' }, { rank: 12, suit: 's' }],
      awardTitle: addressOnly || mode === 'pokerroll' ? null : '+2 CARDS',
      awardStartedAtMs: addressOnly || mode === 'pokerroll' ? null : Date.now(),
      awardRevealDelayMs: 900,
    };
    return {
      snapshot,
      event: addressOnly
        ? event(30, 28.4)
        : {
            ...event(30, 30),
            id: 'tv-poker-made',
            proximityFt: 0,
            angleDeg: 0.18,
            hasHoled: true,
            holed: true,
          },
    };
  }

  if (mode === 'pokerreveal') {
    snapshot.mode = 'poker';
    snapshot.title = 'Poker';
    snapshot.scene = { ...baseScene, targetFt: 36, ballColor: '#c8922a' };
    snapshot.transition = {
      modeTitle: 'POKER',
      kicker: 'BETWEEN HOLES',
      title: 'Hole 3 complete',
      headerRight: 'NEXT IN 4',
      players: [
        {
          name: 'Cole',
          accent: '#c8922a',
          detail: 'Pair of Queens · +2 cards',
          badge: 'NEXT UP',
          cards: demoCardCount()
            ? demoCards(demoCardCount() as number)
            : [
                { rank: 14, suit: 's' },
                { rank: 12, suit: 'h' },
                { rank: 12, suit: 's' },
                { rank: 9, suit: 'd' },
              ],
        },
        {
          name: 'Computer',
          accent: '#4fc3f7',
          detail: 'King High · +1 card',
          cards: demoCardCount()
            ? demoCards(Math.max(1, (demoCardCount() as number) - 3), 5)
            : [
                { rank: 13, suit: 'c' },
                { rank: 10, suit: 'h' },
                { rank: 7, suit: 'd' },
              ],
        },
      ],
      actionLabel: 'NEXT HOLE · 4',
    };
  }

  const targetFt = snapshot.targetFt ?? 42;
  return { snapshot, event: event(targetFt, 39.8) };
}

/** A mirror with more than six options: the Challenge home as mode tiles. */
export function challengeHomeMenu(): NonNullable<TvSnapshot['menu']> {
  return {
    id: 'challenge.home',
    heading: 'CHALLENGE',
    options: [
      { id: 'distance', label: 'Distance Challenge', detail: 'Five putts, one distance', emphasis: 'selected' },
      { id: 'sim9', label: 'Simulated 9', detail: 'Nine holes of lag putts' },
      { id: 'ladder', label: 'Putting Ladder', detail: 'Climb without going short' },
      { id: 'startline', label: 'Start Line', detail: 'Pure, slight, wide, major' },
      { id: 'koth', label: 'King of the Hill', detail: 'Five holes, one crown' },
      { id: 'poker', label: 'Poker', detail: 'Earn cards, best hand wins' },
      { id: 'linefour', label: 'Line Four', detail: 'Four in a row by roll' },
      { id: 'turfwar', label: 'Turf War', detail: 'Own the zones' },
      { id: 'friends', label: 'Friend Matches', detail: '2 waiting' },
    ],
  };
}

/**
 * Interrupt fixtures: each base state is the plain fixture with a notice on
 * top, so the presentation is whatever that state already draws.
 *
 * - notice-asleep: Target course, the gate asleep after a failed auto wake.
 * - notice-hit: Target course, a gate hit waiting for the phone's confirm.
 * - notice-readerror: ambient card, "Putt not read" that clears on its own
 *   (20 s here so it can be looked at; the phone sends 3 s).
 * - notice-confirm: the Distance setup mirror with a quit confirm open.
 * - notice-battery: the summary card with the low-battery pill.
 */
function noticeFixture(mode: DemoMode): { snapshot: TvSnapshot; event: TvPuttEvent } {
  if (mode === 'notice-asleep') {
    const base = demoFixture('target');
    base.snapshot.gateConnected = false;
    base.snapshot.notice = {
      id: 'demo-asleep',
      kind: 'gateAsleep',
      text: 'Wake the gate on your device',
      detail: 'The gate went to sleep and did not answer the automatic wake.',
      needsPhone: true,
    };
    return base;
  }
  if (mode === 'notice-hit') {
    const base = demoFixture('target');
    base.snapshot.notice = {
      id: 'demo-hit',
      kind: 'gateHit',
      text: 'Ball hit the gate: confirm on your device',
      detail: 'A confirmed hit records a penalty.',
      tone: 'danger',
      needsPhone: true,
    };
    return base;
  }
  if (mode === 'notice-readerror') {
    const base = demoFixture('ambient');
    base.snapshot.title = 'Target';
    base.snapshot.subtitle = 'Putt to 42 FT';
    base.snapshot.notice = {
      id: 'demo-readerror',
      kind: 'readError',
      text: 'Putt not read, try again',
      detail: 'Roll it through the middle of the gate.',
      needsPhone: false,
      expiresAtMs: Date.now() + 20_000,
    };
    return base;
  }
  if (mode === 'notice-confirm') {
    const base = demoFixture('mirror');
    base.snapshot.notice = {
      id: 'demo-confirm',
      kind: 'confirm',
      text: 'Confirm on your device',
      detail: 'Quit this round?',
      needsPhone: true,
    };
    return base;
  }
  const base = demoFixture('summary');
  base.snapshot.notice = {
    id: 'demo-battery',
    kind: 'battery',
    text: 'Gate battery low',
    detail: 'Charge the gate after this round.',
    needsPhone: false,
  };
  return base;
}

/**
 * The football field course (packages/putt-scene/src/footballScene.ts) on the
 * offense fixture: the field at the spot, the first-down line, the end zones
 * in the teams' colors (Kelly's red attacking Cole's white, painted dark with
 * white letters), and the offense's cups at their putting distances. Near the
 * goal the cups the field has no room for are off the green, as in
 * footballCupsOnGreen.
 */
/** The TYPICAL and BEST columns (app FOOTBALL_CUP_SPECS, v4). */
function footballFieldFixture(mode: DemoMode): { snapshot: TvSnapshot; event: TvPuttEvent } {
  const fixture = demoFixture('football');
  const spot = mode === 'footballfield-mid'
    ? { yl: 50, down: 3, togo: 4, label: '3RD & 4 . MIDFIELD' }
    : mode === 'footballfield-goal'
      ? { yl: 92, down: 1, togo: 8, label: '1ST & GOAL . OPP 8' }
      : { yl: 20, down: 1, togo: 10, label: '1ST & 10 . OWN 20' };
  const goalYards = 100 - spot.yl;
  // v5: the rings follow the plays table, as FootballHost draws them from one
  // rule (footballCupViews): 3 ft fresh, 1.5 ft seen once (the full ring a
  // faint dashed outline), no ring at HOLE IT, gone plays off the green.
  const plays = [
    { key: 'inside', label: 'INSIDE RUN', distanceFt: 9, stage: 0, color: '#f5f8eb' },
    { key: 'outside', label: 'OUTSIDE RUN', distanceFt: 18, stage: 1, color: '#f5f8eb' },
    { key: 'short', label: 'SHORT PASS', distanceFt: 27, stage: 0, color: '#cfe3ff' },
    { key: 'medium', label: 'MEDIUM PASS', distanceFt: 36, stage: 2, color: '#cfe3ff' },
    { key: 'deep', label: 'DEEP PASS', distanceFt: 45, stage: mode === 'footballfield-goal' ? 3 : 0, color: '#cfe3ff' },
  ];
  const ringFt = [3, 1.5, 0];
  const cups = plays
    .filter((play) => play.stage < 3)
    .map((play) => {
      const ring = ringFt[play.stage];
      return { id: `cup-${play.key}`, distanceFt: play.distanceFt, ...(ring > 0 ? { ringRadiusFt: ring } : {}), color: play.color, opacity: 0.75 };
    });
  const table = plays.map((play) => ({
    key: play.key, label: play.label, distance: `${play.distanceFt} FT`,
    need: (['3 FT', '1.5 FT', 'HOLE IT', 'GONE'] as const)[play.stage],
    tone: (['fresh', 'seen', 'hole', 'gone'] as const)[play.stage],
  }));
  const scene = fixture.snapshot.scene as Extract<TvScene, { kind: 'putt' }>;
  fixture.snapshot.scene = {
    ...scene,
    targetFt: 45,
    extraCups: cups,
    // The standing left-rail markers FootballHost sends (footballDistanceMarkers):
    // green 3 ft, yellow 1.5, red hole it, gray gone.
    rolloutBands: plays.map((play) => ({
      id: `marker-${play.key}`,
      minFt: play.distanceFt,
      maxFt: play.distanceFt,
      label: String(play.distanceFt),
      labelOrientation: 'billboard' as const,
      lineColor: ['#FFFFFF', '#E3B341', '#F0605D', '#8A94A0'][play.stage],
      labelOnly: true,
      labelScale: 2.5,
    })),
    extraBalls: [],
    footballField: {
      scrimmageYl: spot.yl,
      firstDownYl: spot.togo >= goalYards ? null : spot.yl + spot.togo,
      aheadEndZone: { color: '#ffffff', name: 'COLE' },
      behindEndZone: { color: '#f0605d', name: 'KELLY' },
    },
  };
  const game = fixture.snapshot.game as Record<string, unknown> | null | undefined;
  if (game) {
    Object.assign(game, {
      yl: spot.yl, down: spot.down, togo: spot.togo, downLabel: spot.label, status: spot.label,
      role: 'offense', offense: 0, putterTeam: 0, moment: null, table,
    });
  }
  fixture.snapshot.title = 'FOOTBALL';
  return fixture;
}

/**
 * Darts-Cricket's board, scoreboard and marks table, built with the app's own
 * engine and view (cricket.ts, cricketView.ts) so the fixture is what a phone
 * would publish.
 */
function cricketFixture(mode: DemoMode, addressOnly: boolean): { snapshot: TvSnapshot; event: TvPuttEvent } {
  const play = (state: CricketState, hits: CricketHit[]) => hits.reduce((current, hit) => applyCricketHit(current, hit).state, state);
  const n = (target: 15 | 16 | 17 | 18 | 19 | 20, multiplier: 1 | 2 | 3 = 3): CricketHit => ({ kind: 'number', target, multiplier });
  const miss: CricketHit = { kind: 'miss' };
  const names = mode === 'cricketmid' ? ['Cole', 'Kelly', 'Stu'] : mode === 'cricket4' ? ['Cole', 'Kelly', 'Stu', 'Mom'] : ['Cole', 'Computer'];
  let state = createCricketGame({ players: names.length, length: 'standard' });
  if (mode === 'cricketmid') {
    // Round 4, Cole up: 17 and 20 closed for everyone (crossed out, blacked
    // out, labels gone), Cole 114 points ahead on 19 and 20.
    state = play(state, [
      n(20), n(19), n(19, 2),       n(20), n(17), n(18, 1),       n(20, 2), n(20, 1), n(17),
      n(17), n(19), miss,           n(18, 2), n(15, 1), miss,      n(17), miss, n(16, 2),
      n(19, 1), n(18), { kind: 'bull', marks: 1 }, n(16), n(15, 2), miss, n(19, 1), n(18, 2), miss,
    ]);
  } else if (mode === 'cricket4') {
    state = play(state, [n(20), n(19, 2), miss, n(18), miss, miss, n(20, 1), n(17, 2), miss, miss, n(15), miss]);
  } else if (mode === 'cricketbull' || mode === 'cricketpopup') {
    state = play(state, [n(20), n(19), n(18, 1), n(20, 2), n(17), miss, n(18, 1)]);
  }
  // The putt the popup describes, applied as the phone applies it at the stop.
  let popup = null as ReturnType<typeof cricketDartPopup> | null;
  let putt = { distanceFt: 36.4, angleDeg: 0.12 };
  if (mode === 'cricketbull' || mode === 'cricketpopup') {
    const hit: CricketHit = mode === 'cricketbull' ? { kind: 'bull', marks: 2 } : n(18, 3);
    putt = mode === 'cricketbull' ? { distanceFt: 55.3, angleDeg: 0.25 } : { distanceFt: 36.6, angleDeg: -0.18 };
    const applied = applyCricketHit(state, hit);
    state = applied.state;
    popup = addressOnly ? null : cricketDartPopup(applied.dart!, applied.turnEnded ? applied.state.lastTurn : null, 7);
  }
  const colors = ['#FFFFFF', '#F0605D', '#4FC3F7', '#E8C46B'].slice(0, names.length);
  const game = cricketTvGameState(state, { names, colors, hostPhase: 'putt', popup });
  const scene: Extract<TvScene, { kind: 'putt' }> = {
    ...baseScene,
    targetFt: 55,
    hideHole: true,
    hideFlag: true,
    hideGimme: true,
    hideProxLabel: true,
    reviewFly: 'never',
    holdFinalFrame: true,
    rolloutBands: cricketRolloutBands(state),
    dartBoard: cricketDartBoard(state),
    ballColor: colors[state.turn] ?? '#FFFFFF',
    showAvgProx: false,
    avgProxFt: null,
  };
  const snapshot: TvSnapshot = {
    mode: 'cricket',
    title: 'DARTS: CRICKET',
    playerName: names[state.turn],
    targetFt: 55,
    gateConnected: true,
    scene,
    game: game as unknown as TvSnapshot['game'],
  };
  const ev: TvPuttEvent = {
    kind: 'putt',
    id: `tv-cricket-${mode}`,
    createdAtMs: Date.now(),
    speedMps: mode === 'cricketbull' ? 3.6 : 2.95,
    distanceFt: putt.distanceFt,
    angleDeg: putt.angleDeg,
    proximityFt: null,
    targetFt: null,
  };
  return { snapshot, event: ev };
}

function normalizeMode(rawMode: string | null): DemoMode {
  const normalized = (rawMode || 'target').toLowerCase();
  return DEMO_MODES.has(normalized) ? normalized as DemoMode : 'target';
}

/* ---------------------------------------------------------------------------
 * Sim 9 fixtures, field for field what the app publishes today: the scene is
 * ExternalPuttScene's payload (app/LagKing/src/components/putt3d/PuttScene.tsx),
 * the game is the screen's `tvGameState` literal, the transition its
 * `studioTransition`. Only `sim9-match-full` adds fields the app does not
 * send yet (protocol.ts, "Publishing gap"), to show them lighting up.
 * ------------------------------------------------------------------------ */

/** SIM9_BALL_COLORS (app/LagKing/src/sim9/ballColors.ts), first three. */
const SIM9_WHITE = '#F5F5F5';
const SIM9_YELLOW = '#FFD43B';

function sim9Scene(
  holeNumber: number,
  targetFt: number,
  overrides: Partial<Extract<TvScene, { kind: 'putt' }>> = {},
): Extract<TvScene, { kind: 'putt' }> {
  return {
    kind: 'putt',
    targetFt,
    stimp: 10,
    hideHole: false,
    hideFlag: false,
    hideGimme: false,
    hideProxLabel: false,
    // Sim9PlayScreen and Sim9StrokePlayScreen pass hideDefaultHud: the phone
    // draws its own HUD. The TV ignores it and draws the details.
    hideDefaultHud: true,
    // The Sim 9 gimme the phone sends (5 ft since 2026-10-19).
    gimmeRadiusFt: 5,
    innerGimmeRadiusFt: 0,
    reviewFly: 'skipOnMake',
    holdFinalFrame: false,
    targetLineFt: null,
    hideTargetLine: false,
    targetLineResultKnown: false,
    targetLinePassed: null,
    finishLineFt: null,
    finishLinePassed: false,
    rolloutBands: [],
    ballColor: '#FFFFFF',
    extraBalls: [],
    ballLabel: null,
    cupPopup: null,
    challengeProgress: null,
    avgProxFt: null,
    showAvgProx: true,
    status: null,
    courseId: 'cedarridge',
    sceneryId: 'cedar',
    holeId: String(holeNumber),
    ...overrides,
  };
}

function sim9Snapshot(scene: Extract<TvScene, { kind: 'putt' }>): TvSnapshot {
  return {
    screen: 'putt3d',
    mode: 'challenge',
    title: 'Simulated 9',
    playerName: 'Cole',
    targetFt: scene.targetFt,
    gateConnected: true,
    sleep: 'putting',
    phoneAwake: false,
    presentation: { kind: 'course', routeId: null, inputOwner: 'phone' },
    scene,
  };
}

/** Cedar Ridge: 10, 22, 34, 16, 42, 28, 60, 36, 18 (par 2 except hole 7). */
const CEDAR_PARS = [2, 2, 2, 2, 2, 2, 3, 2, 2];

function sim9Fixture(mode: DemoMode): { snapshot: TvSnapshot; event: TvPuttEvent } {
  if (mode === 'sim9' || mode === 'sim9-scorecard') {
    // Sim9PlayScreen, hole 3 (34 ft), two holes played at +1. The putt left
    // 2.4 ft: inside the gimme, the hole is done.
    const snapshot = sim9Snapshot(sim9Scene(3, 34));
    snapshot.game = {
      kind: 'sim9',
      courseName: 'Cedar Ridge',
      hole: 3,
      holes: 9,
      par: 2,
      requiredFt: 34,
      strokes: 0,
      status: '+1',
      scoreLabel: 'ROUND',
    };
    const putt: TvPuttEvent = {
      kind: 'putt',
      id: 'tv-sim9-solo',
      createdAtMs: Date.now(),
      speedMps: 2.55,
      distanceFt: 31.8,
      angleDeg: 1.1,
      proximityFt: 2.4,
      targetFt: 34,
      hasHoled: true,
      holed: false,
    };
    if (mode === 'sim9-scorecard') {
      // The auto scorecard after hole 3 (a bogey 3 on a par 2).
      const scores = [2, 3, 3];
      snapshot.transition = {
        modeTitle: 'SIM 9',
        kicker: 'BOGEY',
        title: 'Cedar Ridge',
        headerRight: 'HOLE 3/9',
        stats: CEDAR_PARS.map((par, index) => ({
          label: `HOLE ${index + 1} · PAR ${par}`,
          value: scores[index] == null ? '—' : String(scores[index]),
          tone: 'neutral' as const,
        })),
        actionLabel: 'HOLE 4 IN 5 S',
        countdownEndsAtMs: Date.now() + 5000,
        countdownLabel: 'Hole 4',
      };
    }
    return { snapshot, event: putt };
  }

  // Sim9StrokePlayScreen, two players, 'away' order, hole 2 (22 ft): Cole
  // left his first putt 5.1 ft out and waits (a labeled ball); Kelly is
  // putting. Cole won hole 1, so the status sentence is "COLE 1 UP".
  const scene = sim9Scene(2, 22, {
    ballColor: SIM9_YELLOW,
    extraBalls: [{ xFt: 0.8, zFt: -16.9, color: SIM9_WHITE, label: 'Cole' }],
  });
  const snapshot = sim9Snapshot(scene);
  snapshot.game = {
    kind: 'sim9',
    courseName: 'Cedar Ridge',
    hole: 2,
    holes: 9,
    par: 2,
    requiredFt: 22,
    strokes: 0,
    status: 'COLE 1 UP',
    scoreLabel: 'ROUND',
    playerName: 'Kelly',
    ...(mode === 'sim9-match-full'
      ? {
          playerColor: SIM9_YELLOW,
          roundToPar: '+1',
          players: [
            { name: 'Cole', color: SIM9_WHITE, toPar: 0, strokes: 1, active: false },
            { name: 'Kelly', color: SIM9_YELLOW, toPar: 1, strokes: 0, active: true },
          ],
        }
      : {}),
  };
  if (mode === 'sim9-match-scorecard') {
    snapshot.transition = {
      modeTitle: 'SIM 9',
      kicker: 'COLE 1 UP',
      title: 'Cedar Ridge',
      headerRight: 'THRU 2',
      table: {
        columns: ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'TOTAL'],
        rows: [
          { label: 'Cole', accent: SIM9_WHITE, values: ['2', '1', '—', '—', '—', '—', '—', '—', '—', '3'] },
          { label: 'Kelly', accent: SIM9_YELLOW, values: ['3', '2', '—', '—', '—', '—', '—', '—', '—', '5'] },
        ],
      },
      actionLabel: 'Cole UP NEXT · 5 S',
      countdownEndsAtMs: Date.now() + 5000,
      countdownLabel: 'Cole up next',
    };
  }
  if (mode === 'sim9-challenge-hole') {
    // What Sim9MatchResponseScreen sends between holes of a friend challenge:
    // who won the hole, the whole scorecard, and the player's own round to
    // par in the header.
    snapshot.transition = {
      modeTitle: 'SIM 9 MATCH',
      kicker: 'HOLE 3',
      title: '@COLE WINS THE HOLE',
      headerRight: 'THRU 3 · YOUR ROUND +1',
      detail: '@cole 2 · @kelly 3  ·  @COLE LEADS 2–1',
      table: {
        columns: ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'TOTAL'],
        rows: [
          { label: '@kelly', accent: SIM9_YELLOW, values: ['2', '3', '3', '—', '—', '—', '—', '—', '—', '8'] },
          { label: 'You', accent: SIM9_WHITE, values: ['2', '3', '2', '—', '—', '—', '—', '—', '—', '7'] },
        ],
      },
      actionLabel: 'NEXT HOLE · AUTO-ADVANCES IN 5 S',
      countdownEndsAtMs: Date.now() + 5000,
      countdownLabel: 'Next hole',
    };
  }
  return {
    snapshot,
    event: {
      kind: 'putt',
      id: 'tv-sim9-match',
      createdAtMs: Date.now(),
      speedMps: 2.1,
      distanceFt: 22.4,
      angleDeg: -0.3,
      proximityFt: 0,
      targetFt: 22,
      hasHoled: true,
      holed: true,
    },
  };
}

/** `&course=`: the Sim 9 HUD names the chosen course, not the fixture's. */
export function withPreviewCourse<T extends { snapshot: TvSnapshot }>(fixture: T, courseId: string | null): T {
  const course = courseId ? SIM9_COURSES.find((c) => c.id === courseId) : undefined;
  const game = fixture.snapshot.game;
  if (!course || game?.kind !== 'sim9') return fixture;
  return { ...fixture, snapshot: { ...fixture.snapshot, game: { ...game, courseName: course.name } } };
}

// ---------------------------------------------------------------------------
// ?demo=footballplay: one Football snap played as a script, so the
// sequence (FOOTBALL_DESIGN_PROPOSAL.md "Transitions (2026-10-17)") can be
// watched on the TV page: the offense's putt with every marker; the defense
// comes up with only the 27 and the proximity to beat in the scoreboard's
// status line; the defense's putt;
// the ball at rest (the 27 turns yellow); a slight zoom out with the markers
// fading; the blue and yellow lines moving to the new spot on a field that
// holds still; the 2 s result popup; the camera easing back in under the
// turn card; the field sliding the new line back to the ball; the next
// snap's markers fading in. Each step is what the phone would publish.
// ---------------------------------------------------------------------------

export interface FootballPlayStep {
  name: string;
  snapshot: TvSnapshot;
  event: TvPuttEvent | null;
  holdMs: number;
}

const PLAY_COLORS = ['#FFFFFF', '#E3B341', '#F0605D', '#8A94A0'] as const;
const PLAY_CUPS: Array<{ key: string; ft: number; run: boolean }> = [
  { key: 'inside', ft: 9, run: true },
  { key: 'outside', ft: 18, run: true },
  { key: 'short', ft: 27, run: false },
  { key: 'medium', ft: 36, run: false },
  { key: 'deep', ft: 45, run: false },
];

export function footballPlayScript(): FootballPlayStep[] {
  const fade = 350;
  const markers = (stages: number[], show: (key: string) => boolean, color?: (key: string, stage: number) => string) =>
    PLAY_CUPS.map((cup, i) => ({
      id: `marker-${cup.key}`, minFt: cup.ft, maxFt: cup.ft, label: String(cup.ft),
      labelOrientation: 'billboard' as const, labelOnly: true, labelScale: 2.5,
      lineColor: color ? color(cup.key, stages[i]) : PLAY_COLORS[stages[i]],
      hidden: !show(cup.key), fadeMs: fade,
    }));
  const rings = (stages: number[], show: boolean) =>
    PLAY_CUPS.map((cup, i) => {
      const ring = [3, 1.5, 0][stages[i]] ?? 0;
      return {
        id: `cup-${cup.key}`, distanceFt: cup.ft, ...(ring > 0 ? { ringRadiusFt: ring } : {}),
        color: cup.run ? '#f5f8eb' : '#cfe3ff', opacity: stages[i] === 2 ? 0.95 : 0.75, lit: stages[i] === 2,
        hidden: !show, fadeMs: fade, holeStyle: 'field' as const,
      };
    });
  const before = [0, 0, 0, 1, 0];
  const after = [0, 0, 1, 1, 0];
  const field = (yl: number, firstDownYl: number) => ({ scrimmageYl: yl, firstDownYl, aheadEndZone: { color: '#ffffff', name: 'COLE' }, behindEndZone: { color: '#f0605d', name: 'KELLY' } });
  const offensePutt: TvPuttEvent = { ...event(27, 27.3), id: 'footballplay-offense', angleDeg: 0.6 };
  const defensePutt: TvPuttEvent = { ...event(27, 25.9), id: 'footballplay-defense', angleDeg: -0.4 };
  const step = (name: string, holdMs: number, ev: TvPuttEvent | null, scene: Record<string, unknown>, game: Record<string, unknown>): FootballPlayStep => {
    const base = demoFixture('football');
    const snapshot = JSON.parse(JSON.stringify(base.snapshot)) as TvSnapshot;
    snapshot.scene = { ...(snapshot.scene as Extract<TvScene, { kind: 'putt' }>), ...scene } as TvScene;
    snapshot.game = { ...(snapshot.game as Record<string, unknown>), ...game } as TvSnapshot['game'];
    return { name, snapshot, event: ev, holdMs };
  };
  const offenseGame = { role: 'offense', offense: 0, putterTeam: 0, yl: 53, down: 1, togo: 10, downLabel: '1ST & 10 . OPP 47', clockLabel: 'DRIVE 2 OF 3', status: 'PUTT TO A CUP · STOP IN ITS RING', lastSnap: null, moment: null, targetFt: null };
  const moved = { frameYl: 53, scrimmageYl: 62, firstDownYl: 72 };
  const nextGame = { ...offenseGame, yl: 62, downLabel: '1ST & 10 . OPP 38', clockLabel: 'DRIVE 2 OF 3', status: 'PUTT TO A CUP · STOP IN ITS RING' };
  const resultGame = {
    ...offenseGame, role: 'offense', status: 'SHORT PASS · 9 YARDS · FIRST DOWN', clockLabel: 'DRIVE 2 OF 3',
    lastSnap: { team: 0, fromYl: 53, toYl: 62, yards: 9, result: 'complete', label: 'COMPLETE', line: 'SHORT PASS · 9 YARDS', touchdown: false, turnover: false, firstDown: true },
  };
  const ghost = [{ xFt: 0.28, zFt: -27.3, color: '#f0605d' }];
  const answerCups = [
    ...rings(before, false).map((cup) => (cup.id === 'cup-short' ? { id: 'cup-short', distanceFt: 27, color: '#c8922a', opacity: 0.9, lit: true, flag: true, flagColor: '#FFFFFF', hidden: false, fadeMs: fade, holeStyle: 'field' as const } : cup)),
    { id: 'answer', distanceFt: 27, ringRadiusFt: 0.4, color: '#c8922a', opacity: 0.95, ringOnly: true, hidden: false, fadeMs: fade },
  ];
  const defenseGame = { ...offenseGame, role: 'defense', putterTeam: 1, status: 'BEAT 0.4 FT AT THE 27 FT CUP', targetFt: 27 };
  const restScene = { targetFt: 45, ballColor: '#ffffff', extraBalls: ghost, rolloutBands: markers(after, () => false), extraCups: rings(after, false) };
  return [
    step('offense', 4600, offensePutt, {
      targetFt: 45, ballColor: '#f0605d', extraBalls: [], footballField: field(53, 63),
      rolloutBands: markers(before, () => true), extraCups: rings(before, true),
    }, offenseGame),
    // The defense comes up: only the answered cup, and the proximity to beat
    // before its input window opens (under the 1.5 s turn card on the phone).
    step('defense up', 1500, null, {
      targetFt: 45, ballColor: '#ffffff', extraBalls: ghost, footballField: field(53, 63),
      rolloutBands: markers(before, () => false), extraCups: answerCups,
    }, { ...defenseGame, phase: 'handoff' }),
    step('answer', 4200, defensePutt, {
      targetFt: 45, ballColor: '#ffffff', extraBalls: ghost, footballField: field(53, 63),
      rolloutBands: markers(before, () => false), extraCups: answerCups,
    }, { ...defenseGame, phase: 'resolving' }),
    // At rest: the answered cup's rings fade; nothing else comes back.
    step('rest', 1000, defensePutt, {
      ...restScene, footballField: field(53, 63),
      rolloutBands: markers(after, () => false), extraCups: answerCups.map((cup) => ({ ...cup, hidden: true })),
    }, resultGame),
    // A slight zoom out; the markers fade.
    step('zoom out', 700, defensePutt, { ...restScene, footballField: { ...field(53, 63), wide: true } }, resultGame),
    // The lines move to the new spot; the field holds still.
    step('lines move', 1000, defensePutt, { ...restScene, footballField: { ...field(53, 63), ...moved, wide: true } }, { ...resultGame, downLabel: nextGame.downLabel }),
    step('result popup', 2000, defensePutt, { ...restScene, footballField: { ...field(53, 63), ...moved, wide: true } }, {
      ...resultGame, downLabel: nextGame.downLabel, popup: { label: '9 YARD PASS, FIRST DOWN!', tone: 'gold', key: 1 },
    }),
    // The popup goes by itself. Under the turn card the next snap is set up
    // in one cut (the ball back on the line, the new field) while the camera
    // eases back in; then its markers and cups fade in, before the putt.
    step('turn card', 1000, null, {
      targetFt: 45, ballColor: '#f0605d', extraBalls: [], footballField: field(62, 72),
      rolloutBands: markers(after, () => false), extraCups: [],
    }, { ...nextGame, phase: 'handoff' }),
    step('next snap', 3000, null, {
      targetFt: 45, ballColor: '#f0605d', extraBalls: [], footballField: field(62, 72),
      rolloutBands: markers(after, () => true), extraCups: rings(after, true),
    }, nextGame),
  ];
}

// ---------------------------------------------------------------------------
// ?demo=baseballhr: a home run swing played as the phone publishes it, to
// check the two-ball bug (Cole, 2026-10-19: "for home runs only, the ball
// renders near the hole a split second before it stops rolling"). A 45.6 ft
// roll at 3.9 m/s lasts about 7.1 s. Phones before the fix heard "settled"
// at their capped 6.5 s and published the fielding state with the batter's
// ball at its rest spot (BaseballHost commitPending) while the TV was still
// rolling, so this script publishes it at 6.5 s too. The scene must keep one
// ball (packages/putt-scene/src/replayTiming.ts staticBallHiddenByReplay).
// ---------------------------------------------------------------------------

/** The old phone's capped settle, when the fielding state went out. */
export const BASEBALL_HR_OLD_SETTLE_MS = 6500;

export function baseballHomeRunScript(): FootballPlayStep[] {
  const distanceFt = 45.6;
  const angleDeg = 0.35;
  const swing: TvPuttEvent = {
    ...event(45, distanceFt),
    id: 'baseballhr-swing',
    speedMps: 3.9,
    angleDeg,
    proximityFt: null,
  };
  // The rolling ball keeps the batter's color until the turn changes
  // (MultiplayerPuttStage freezes the capture-time tint).
  const step = (name: string, holdMs: number, ev: TvPuttEvent | null, fielding: boolean): FootballPlayStep => {
    const base = demoFixture('baseball');
    const snapshot = JSON.parse(JSON.stringify(base.snapshot)) as TvSnapshot;
    const state: BaseballState = {
      ...createBaseballGame(3),
      phase: fielding ? 'fielding' : 'batting',
      pending: fielding ? { kind: 'hit', hole: 45, leaveFt: 0.6 } : null,
    };
    snapshot.scene = {
      ...(snapshot.scene as Extract<TvScene, { kind: 'putt' }>),
      ballColor: fielding && !ev ? '#ffffff' : '#f0605d',
      extraCups: baseballExtraCups(state),
      rolloutBands: baseballDistanceMarkers(state),
      // BaseballHost's ghost: lateralAt(distance, angle), z = -distance.
      extraBalls: fielding
        ? [{ xFt: distanceFt * Math.tan((angleDeg * Math.PI) / 180), zFt: -distanceFt, color: '#f0605d' }]
        : [],
      holedAtFt: null,
    } as TvScene;
    snapshot.game = {
      ...(snapshot.game as Record<string, unknown>),
      role: fielding ? 'fielding' : 'batting',
      putterTeam: fielding ? 1 : 0,
      phase: fielding ? 'handoff' : 'resolving',
      bases: [false, false, false],
      pending: fielding ? { kind: 'hit', hole: 45, leaveFt: 0.6, bases: 4 } : null,
      status: fielding ? 'GET INSIDE 0.6 FT FROM 45 FT' : 'SWING',
    } as TvSnapshot['game'];
    return { name, snapshot, event: ev, holdMs };
  };
  return [
    step('swing', BASEBALL_HR_OLD_SETTLE_MS, swing, false),
    // The old phone's settle: the fielding state with the batter's ball,
    // published while the TV's ball still has about 0.6 s to roll.
    step('phone settled', 3500, swing, true),
    step('fielder up', 3000, null, true),
  ];
}
